import { captureRemixErrorBoundaryError } from '@sentry/remix';
import type { LinksFunction, LoaderFunctionArgs } from '@remix-run/cloudflare';
import { cssBundleHref } from '@remix-run/css-bundle';
import {
  Link,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  json,
  useLoaderData,
  useLocation,
  useRouteError,
  useRouteLoaderData,
} from '@remix-run/react';
import './routes/reset.css';
import styles from './tailwind.css?url';
import './styles.css';

import { IntercomProvider } from 'react-use-intercom';
import { ApplicationEnvironment } from './environment';
import { useInitializeAmplitude } from './hooks/use-initialize-amplitude';
import { environmentState } from './state/environment';
import { getOrigin } from './get-origin';
import { keywords } from './seo/keywords';
import { propagateError } from './propagate-error';

import './sprites/sprites.svg';
import { Snackbar } from './components/snackbar/snackbar';
import { ErrorAnimation } from './components/404';
import { WhatsappButton } from './components/whatsapp/whatsapp-button';
import { EnhancedInquiryModal } from './components/inquiry/enhanced-inquiry-modal';
import { useState, useEffect } from 'react';
import { Banner } from './components/banner/banner';
import { MobileNavigationDialog } from './components/camp67-header/mobile-navigation-dialog';
import { Countries, Currencies } from 'country-to-currency';
import { ExchangeRate, exchangeRate } from './state/prices';
import { reservationStore as localReservationStore } from './state/reservation-store';
import { analyticsState } from './state/amplitude';
import { NivaasOffering } from './types/offerings';
import { initializeOfferings } from './state/offerings';
import {
  initializeOfferings as initializeSharedOfferings,
  initializeAnalytics,
  initializeEnvironment,
  initializePackages,
  reservationStore as sharedReservationStore,
  exchangeRate as sharedExchangeRate,
  type BrandConfig,
  type NivaasPackage,
} from '@kumbh/booking-components';
import i18nServer from './i18n/i18n.server';
import { useChangeLanguage } from 'remix-i18next/react';
import { FACEBOOK, INSTAGRAM, TWITTER, YOUTUBE } from './constants/socials';
import Sprite from './sprites/sprite';
import { onOpenInquiry } from './state/inquiry-modal';
import { useSnapshot } from 'valtio';
import { onCloseTentModal, tentModal } from './state/tent-modal';
import { TentExperienceModalV2 } from './components/booking-form/tent-experience-modal-v2';
export const handle = { i18n: ['translation'] };

export const links: LinksFunction = () => [
  { rel: 'stylesheet', href: styles },
  ...(cssBundleHref ? [{ rel: 'stylesheet', href: cssBundleHref }] : []),
];

interface OfferingsResponse {
  offerings: NivaasOffering[];
}

interface PackagesResponse {
  packages: NivaasPackage[];
}

interface WorkspaceSettingsResponse {
  supportsOccupancyPricing: boolean;
}

export const loader = async ({ request, context }: LoaderFunctionArgs) => {
  const env = context.cloudflare.env as ApplicationEnvironment;
  const requestCookie = request.headers.get('cookie') ?? '';

  const country = context.cloudflare.cf.country as Countries;
  const locale = await i18nServer.getLocale(request);

  try {
    // Fetch session and offerings in parallel
    const sessionResponse = await fetch(`${env.API_URL}/api/session`, {
      headers: {
        origin: getOrigin(request),
        Cookie: requestCookie,
        'X-User-Country': country || '',
      },
    });

    const cookie = sessionResponse.headers.get('set-cookie');

    if (!sessionResponse.ok || !cookie) {
      return json({
        locale,
        intercomKey: env.INTERCOM_APP_KEY,
        amplitudeKey: env.AMPLITUDE_API_KEY,
        offerings: [],
        packages: [],
        prices: { rate: 1, symbol: '₹', currency: 'INR' as Currencies },
        countryCode: country ?? 'IN',
        apiUrl: env.API_URL,
        paypalClientId: env.PAYPAL_CLIENT_ID,
        supportsOccupancyPricing: false,
      });
    }

    const prices = await sessionResponse.json<ExchangeRate>();

    // Pass user's country to worker via header so it can return correct exchange rate
    const countryCode: Countries =
      country ?? (sessionResponse.headers.get('X-Country') as Countries) ?? 'USD';

    const offeringsResponse = await fetch(`${env.API_URL}/api/offerings`, {
      headers: {
        origin: getOrigin(request),
        Cookie: cookie,
        'X-User-Country': country || '',
      },
    });

    if (!offeringsResponse.ok) {
      return json(
        {
          locale,
          intercomKey: env.INTERCOM_APP_KEY,
          amplitudeKey: env.AMPLITUDE_API_KEY,
          offerings: [],
          packages: [],
          prices: { rate: 1, symbol: '₹', currency: 'INR' as Currencies },
          countryCode: country ?? 'IN',
          apiUrl: env.API_URL,
          paypalClientId: env.PAYPAL_CLIENT_ID,
          supportsOccupancyPricing: false,
        },
        { headers: { 'Set-Cookie': cookie, 'X-Country': country ?? 'IN' } },
      );
    }

    // Parse offerings response
    let offerings: NivaasOffering[] = [];
    try {
      const parsed = (await offeringsResponse.json()) as OfferingsResponse;
      if (Array.isArray(parsed.offerings)) {
        offerings = parsed.offerings;
      }
    } catch (e) {
      console.error('Failed to parse offerings response:', e);
    }

    // Fetch packages
    let packages: NivaasPackage[] = [];
    try {
      const packagesResponse = await fetch(`${env.API_URL}/api/packages`, {
        headers: {
          origin: getOrigin(request),
          Cookie: cookie,
          'X-User-Country': country || '',
        },
      });
      if (packagesResponse.ok) {
        const parsed = (await packagesResponse.json()) as PackagesResponse;
        if (Array.isArray(parsed.packages)) {
          packages = parsed.packages;
        }
      }
    } catch (e) {
      console.error('Failed to fetch packages:', e);
    }

    // Fetch workspace settings (for occupancy pricing support, etc.)
    let supportsOccupancyPricing = false;
    try {
      const settingsResponse = await fetch(`${env.API_URL}/api/workspace-settings`, {
        headers: {
          origin: getOrigin(request),
          Cookie: cookie,
        },
      });
      if (settingsResponse.ok) {
        const parsed = (await settingsResponse.json()) as WorkspaceSettingsResponse;
        supportsOccupancyPricing = parsed.supportsOccupancyPricing ?? false;
      }
    } catch (e) {
      console.error('Failed to fetch workspace settings:', e);
    }

    const keys = {
      amplitudeKey: env.AMPLITUDE_API_KEY,
      // statsigKey: env.STATSIG_API_KEY,
      intercomKey: env.INTERCOM_APP_KEY,
      apiUrl: env.API_URL,
      paypalClientId: env.PAYPAL_CLIENT_ID,
      countryCode,
      prices,
      locale,
      offerings,
      packages,
      supportsOccupancyPricing,
    };

    return json(keys, {
      headers: {
        'Set-Cookie': cookie,
        'X-Country': sessionResponse.headers.get('X-Country') ?? '',
      },
    });
  } catch (err) {
    propagateError(err);
    throw json('Internal Server Error', 500);
  }
};

export const ErrorBoundary = () => {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);

  return (
    // Classname, css modules, css class, none work, have to do style prop.
    <section
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <ErrorAnimation />
      <h1 style={{ fontFamily: 'Fraunces' }}>Oops! Something went wrong.</h1>
      <Link
        prefetch="intent"
        style={{ textDecoration: 'underline', fontFamily: 'DM Sans' }}
        to="/"
      >
        &larr; Go back
      </Link>
    </section>
  );
};

export function Layout({ children }: { children: React.ReactNode }) {
  const loaderData = useRouteLoaderData<typeof loader>('root');

  return (
    <html lang={loaderData?.locale ?? 'en'}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="robots" content="index, follow" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="keywords" content={keywords.join(',')} />
        <meta httpEquiv="Content-language" content="en_US" />
        <meta name="theme-color" content="#ffffff" />
        <link rel="icon" href="/favicon.ico" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" />
        <link rel="preconnect" href="https://imagedelivery.net" />
        <link rel="preconnect" href="https://api.amplitude.com" />
        <link rel="preconnect" href="https://widget.intercom.io" />
        <link rel="preconnect" href="https://js.intercomcdn.com" />

        {/* opengraph tags */}
        <meta
          property="og:title"
          content="camp67: Accommodation for the Magh Mela 2026"
        />
        <meta
          property="og:description"
          content="Book now for Magh Mela 2026. Affordable. Peaceful. Uniquely Kumbh. camp67 is the a premium location to stay during Magh Mela 2026. Book now!"
        />
        <meta property="og:locale" content="en_US" />
        <meta property="og:site_name" content="camp67.com" />
        <meta property="og:url" content="https://camp67.com/" />
        <meta
          property="og:image"
          content="https://imagedelivery.net/Knvys86jJSy4RAoUACMsVg/eea87f85-a66c-4282-aaa8-ca11bec94400/public"
        />
        <meta property="og:image:type" content="image/avif" />
        <meta property="og:type" content="website" />

        {/* twitter tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@camp67_kumbh" />
        <meta
          name="twitter:title"
          content="camp67: Accommodation for the Magh Mela 2026"
        />
        <meta
          name="twitter:description"
          content="Book now for Magh Mela 2026. Affordable. Peaceful. Uniquely Kumbh."
        />
        <meta
          name="twitter:image"
          content="https://imagedelivery.net/Knvys86jJSy4RAoUACMsVg/eea87f85-a66c-4282-aaa8-ca11bec94400/public"
        />
        <Meta />
        <Links />
      </head>
      <body>
        <IntercomProvider
          appId={loaderData?.intercomKey ?? ''}
          initializeDelay={2_000}
          autoBoot={true}
        >
          {children}
          <ScrollRestoration />
          <Scripts />
        </IntercomProvider>
      </body>
      <MobileNavigationDialog />
    </html>
  );
}

export default function App() {
  const {
    amplitudeKey,
    // statsigKey,
    apiUrl,
    paypalClientId,
    countryCode,
    prices,
    locale,
    offerings,
    packages,
    supportsOccupancyPricing,
  } = useLoaderData<typeof loader>();

  useInitializeAmplitude(amplitudeKey);
  // useInitializestatsig(statsigKey);

  // Initialize local environment state
  environmentState.apiUrl = apiUrl;
  environmentState.paypalClientId = paypalClientId;
  environmentState.countryCode = countryCode;

  // Initialize local reservation store
  localReservationStore.initializeCountryCode(countryCode);

  if (prices.currency) {
    exchangeRate.currency = prices.currency;
    exchangeRate.rate = prices.rate;
    exchangeRate.symbol = prices.symbol;

    sharedExchangeRate.currency = prices.currency;
    sharedExchangeRate.rate = prices.rate;
    sharedExchangeRate.symbol = prices.symbol;
  }

  // Initialize shared environment state (used by shared booking components)
  initializeEnvironment({
    apiUrl,
    paypalClientId,
    countryCode,
  });

  // Initialize shared analytics with Amplitude
  initializeAnalytics(
    {
      track: (event, data) => {
        analyticsState.amplitude?.track(event, data);
      },
    },
    'camp67',
  );

  // Configure brand for shared reservation store
  const brandConfig: BrandConfig = {
    brandId: 'camp67',
    offeringStartsFrom: new Date('2025-12-31'),
    offeringEndsAt: new Date('2026-02-20'),
    supportsOccupancyPricing,
  };
  sharedReservationStore.setBrandConfig(brandConfig);
  sharedReservationStore.initializeCountryCode(countryCode);

  // Initialize offerings state (local and shared) AFTER brand config
  // This ensures the reservation store recomputes prices with the new offerings
  if (offerings && offerings.length > 0) {
    initializeOfferings(offerings);
    initializeSharedOfferings(offerings);
    sharedReservationStore.refreshFromOfferings();
  }

  // Initialize packages state for shared booking components
  if (packages && packages.length > 0) {
    initializePackages(packages);
  }

  useChangeLanguage(locale);

  const { type } = useSnapshot(tentModal);

  return (
    <>
      <Banner onOpenInquiry={onOpenInquiry} />
      <Snackbar />
      <WhatsappButton />
      <EnhancedInquiryModal />
      {type && <TentExperienceModalV2 tentType={type} onClose={onCloseTentModal} />}
      <Socials />
      <Outlet />
    </>
  );
}

const Socials = () => {
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const [isBannerVisible, setIsBannerVisible] = useState(false);

  const location = useLocation();

  useEffect(() => {
    const footer = document.querySelector('#footer');

    if (footer) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          setIsFooterVisible(entry.isIntersecting);
        },
        { threshold: 0.1 },
      );

      observer.observe(footer);

      return () => {
        observer.unobserve(footer);
      };
    }
  }, []);

  useEffect(() => {
    const banner = document.querySelector('#camp67-banner');

    if (banner) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          setIsBannerVisible(entry.isIntersecting);
        },
        { threshold: 0.1 },
      );

      observer.observe(banner);

      return () => {
        observer.unobserve(banner);
      };
    }
  }, []);

  if (location.pathname.includes('reservation')) {
    return null;
  }

  if (isFooterVisible || isBannerVisible) return null;

  return (
    <div className="sm:flex hidden flex-col fixed right-0 top-1/2 -translate-y-1/2 bg-white rounded-l-lg px-1 py-3 shadow-lg z-20">
      <ul className="flex flex-col list-none gap-3 xs:pt-0 items-center">
        <li>
          <a href={FACEBOOK} target="_blank" rel="noreferrer">
            <Sprite name="facebook" width="64" height="64" className="w-6 h-6" />
          </a>
        </li>
        <li>
          <a href={INSTAGRAM} target="_blank" rel="noreferrer">
            <img
              src="/instagram.png"
              alt="Instagram"
              className="pointer-events-none select-none w-6 h-6"
            />
          </a>
        </li>
        <li>
          <a href={TWITTER} target="_blank" rel="noreferrer">
            <Sprite
              name="twitter"
              width="64"
              height="64"
              className="w-4 h-4 md:w-6 md:h-6"
            />
          </a>
        </li>
        <li>
          <a href={YOUTUBE} target="_blank" rel="noreferrer">
            <Sprite
              name="youtube"
              width="64"
              height="45"
              className="w-4 h-4 md:w-6 md:h-6"
            />
          </a>
        </li>
      </ul>
    </div>
  );
};
